import { useEffect, useState } from 'react'
import { useCities, useCityPath } from '@/hooks/useCities'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface CityCascadeValue {
  cityId: number
  province: string
  city: string
  district: string
}

interface CityCascadeSelectProps {
  /** 编辑回显：初始省份 ID */
  defaultProvinceId?: string
  /** 编辑回显：初始城市 ID */
  defaultCityId?: string
  /** 编辑回显：初始区县 ID */
  defaultDistrictId?: string
  onChange: (data: CityCascadeValue | null) => void
}

export function CityCascadeSelect({ defaultProvinceId, defaultCityId, defaultDistrictId, onChange }: CityCascadeSelectProps) {
  const [provinceId, setProvinceId] = useState<string>(defaultProvinceId || '')
  const [cityId, setCityId] = useState<string>(defaultCityId || '')
  const [districtId, setDistrictId] = useState<string>(defaultDistrictId || '')

  const { data: provinces } = useCities(0)
  const { data: cities } = useCities(Number(provinceId), !!provinceId)
  const { data: districts } = useCities(Number(cityId), !!cityId)

  // 编辑回显：当外部传入默认值时，初始化选中状态
  useEffect(() => {
    if (defaultProvinceId) setProvinceId(defaultProvinceId)
    if (defaultCityId) setCityId(defaultCityId)
    if (defaultDistrictId) setDistrictId(defaultDistrictId)
  }, [defaultProvinceId, defaultCityId, defaultDistrictId])

  const handleProvinceChange = (val: string) => {
    setProvinceId(val)
    setCityId('')
    setDistrictId('')
    const province = provinces?.find(p => String(p.id) === val)
    if (province) {
      onChange({ cityId: 0, province: province.name, city: '', district: '' })
    }
  }

  const handleCityChange = (val: string) => {
    setCityId(val)
    setDistrictId('')
    const city = cities?.find(c => String(c.id) === val)
    if (city) {
      onChange({ cityId: 0, province: '', city: city.name, district: '' })
    }
  }

  const handleDistrictChange = (val: string) => {
    setDistrictId(val)
    const district = districts?.find(d => String(d.id) === val)
    const province = provinces?.find(p => String(p.id) === provinceId)
    const city = cities?.find(c => String(c.id) === cityId)
    if (district && province && city) {
      onChange({
        cityId: district.id,
        province: province.name,
        city: city.name,
        district: district.name,
      })
    }
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="space-y-2">
        <label htmlFor="city-cascade-province" className="text-sm font-medium">省份</label>
        <Select value={provinceId} onValueChange={handleProvinceChange}>
          <SelectTrigger id="city-cascade-province">
            <SelectValue placeholder="请选择省份" />
          </SelectTrigger>
          <SelectContent>
            {provinces?.map(p => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label htmlFor="city-cascade-city" className="text-sm font-medium">城市</label>
        <Select
          value={cityId}
          onValueChange={handleCityChange}
          disabled={!provinceId}
        >
          <SelectTrigger id="city-cascade-city">
            <SelectValue placeholder="请选择城市" />
          </SelectTrigger>
          <SelectContent>
            {cities?.map(c => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label htmlFor="city-cascade-district" className="text-sm font-medium">区县</label>
        <Select
          value={districtId}
          onValueChange={handleDistrictChange}
          disabled={!cityId}
        >
          <SelectTrigger id="city-cascade-district">
            <SelectValue placeholder="请选择区县" />
          </SelectTrigger>
          <SelectContent>
            {districts?.map(d => (
              <SelectItem key={d.id} value={String(d.id)}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

/** 设置级联选择器的初始值（编辑回显用） */
export function useCityCascadeInit(cityId: number | undefined) {
  const [initialized, setInitialized] = useState(false)
  const [initData, setInitData] = useState<{
    provinceId: string
    cityId: string
    districtId: string
  } | null>(null)

  const { data: path } = useCityPath(cityId, !!cityId && cityId > 0)

  useEffect(() => {
    if (path && !initialized) {
      setInitData({
        provinceId: path.province ? String(path.province.id) : '',
        cityId: path.city ? String(path.city.id) : '',
        districtId: path.district ? String(path.district.id) : '',
      })
      setInitialized(true)
    }
  }, [path, initialized])

  return { initData, reset: () => { setInitialized(false); setInitData(null) } }
}
